
import {
  useEffect,
  useState,
} from "react";

import {
  getBookingRates,
} from "../../../api/bookingEngine";

import {
  formatDateKey,
  getDefaultRateDate,
  getStayDates,
} from "../utils/bookingDateUtils";


/* =========================================================
   BOOKING ENGINE PUBLIC RATES
========================================================= */

const useBookingRates = ({
  roomsData = [],
  property = null,
  subdomain = "",
  searchDetails = {},
}) => {

  const [
    ratesByRoom,
    setRatesByRoom,
  ] = useState({});


  const [
    ratesLoading,
    setRatesLoading,
  ] = useState(false);


  /* =========================================================
     TENANT
  ========================================================= */

  const tenantId =
    property?.tenantId ||
    property?.tenant?._id ||
    property?.tenant?.tenantId ||
    "";


  /* =========================================================
     LOAD PUBLIC DATE-WISE RATES
  ========================================================= */

  useEffect(() => {

    let mounted = true;


    const loadRates = async () => {

      /* =====================================================
         NO SUBDOMAIN
      ===================================================== */

      if (!subdomain) {

        if (mounted) {

          setRatesByRoom({});

          setRatesLoading(false);

        }

        return;

      }


      /* =====================================================
         NO DATES
      ===================================================== */

      if (
        !searchDetails?.checkIn ||
        !searchDetails?.checkOut
      ) {

        if (mounted) {

          setRatesByRoom({});

          setRatesLoading(false);

        }

        return;

      }


      /* =====================================================
         VALIDATE DATE RANGE
      ===================================================== */

      if (
        searchDetails.checkIn >=
        searchDetails.checkOut
      ) {

        if (mounted) {

          setRatesByRoom({});

          setRatesLoading(false);

        }

        return;

      }


      try {

        if (mounted) {

          setRatesLoading(true);

        }


        console.log(
          "💰 PUBLIC BOOKING RATES REQUEST:",
          {
            subdomain,
            tenantId,
            checkIn:
              searchDetails.checkIn,
            checkOut:
              searchDetails.checkOut,
          }
        );


        /* ===================================================
           FETCH ALL ROOM TYPE RATES
        =================================================== */

        const results =
          await Promise.all(

            roomsData.map(
              async (room) => {

                const roomTypeId =
                  room?.roomTypeId ||
                  room?.id ||
                  room?._id;


                if (!roomTypeId) {

                  return null;

                }


                try {

                  console.log(
                    "💰 FETCHING PUBLIC RATE:",
                    {
                      room:
                        room?.name,

                      roomTypeId,

                      startDate:
                        searchDetails.checkIn,

                      endDate:
                        searchDetails.checkOut,
                    }
                  );


                  const response =
                    await getBookingRates({

                      subdomain,

                      roomTypeId,

                      startDate:
                        searchDetails.checkIn,

                      endDate:
                        searchDetails.checkOut,

                      ratePlanName:
                        "BAR",

                    });


                  console.log(
                    "💰 PUBLIC RATE RESPONSE:",
                    room?.name,
                    response
                  );


                  const nightlyRates =
                    Array.isArray(
                      response?.nightlyRates
                    )
                      ? response.nightlyRates
                      : [];


                  const normalizedRates =
                    nightlyRates
                      .map(
                        (item) => {

                          const date =
                            formatDateKey(
                              item?.date
                            );


                          const rate =
                            Number(
                              item?.price ??
                              item?.rate ??
                              0
                            );


                          return {

                            date,

                            rate:
                              Number.isFinite(
                                rate
                              )
                                ? rate
                                : 0,

                            source:
                              item?.source ||
                              "PUBLIC_RATE",

                          };

                        }
                      )
                      .filter(
                        (item) =>
                          item.date
                      );


                  return {

                    roomTypeId,

                    rates:
                      normalizedRates,

                    roomTotal:
                      normalizedRates.reduce(
                        (
                          total,
                          item
                        ) =>
                          total +
                          Number(
                            item.rate || 0
                          ),
                        0
                      ),

                  };

                } catch (error) {

                  console.error(
                    "❌ PUBLIC RATE ERROR:",
                    {
                      room:
                        room?.name,

                      roomTypeId,

                      error,
                    }
                  );


                  return {

                    roomTypeId,

                    rates: [],

                    roomTotal: 0,

                  };

                }

              }
            )

          );


        /* ===================================================
           BUILD RATE MAP
        =================================================== */

        const nextRates = {};


        results.forEach(
          (result) => {

            if (!result) {
              return;
            }


            nextRates[
              result.roomTypeId
            ] = {

              rates:
                result.rates,

              roomTotal:
                result.roomTotal,

            };

          }
        );


        if (mounted) {

          setRatesByRoom(
            nextRates
          );

        }


        console.log(
          "💰 FINAL PUBLIC RATE MAP:",
          nextRates
        );


      } catch (error) {

        console.error(
          "❌ BOOKING RATES LOAD ERROR:",
          error
        );


        if (mounted) {

          setRatesByRoom({});

        }

      } finally {

        if (mounted) {

          setRatesLoading(false);

        }

      }

    };


    loadRates();


    return () => {

      mounted = false;

    };

  }, [
    subdomain,
    tenantId,
    roomsData,
    searchDetails?.checkIn,
    searchDetails?.checkOut,
  ]);


  /* =========================================================
     MERGE RATES INTO ROOMS
  ========================================================= */

  const roomsWithRates =
    roomsData.map(
      (room) => {

        const roomTypeId =
          room?.roomTypeId ||
          room?.id ||
          room?._id;


        const roomRateData =
          ratesByRoom[
            roomTypeId
          ];


        /* ===================================================
           SELECTED CHECK-IN DATE
        =================================================== */

        const selectedRateDate =
          formatDateKey(
            searchDetails?.checkIn
          ) ||
          getDefaultRateDate();


        /* ===================================================
           FALLBACK RATE
        =================================================== */

        const fallbackRate =
          Number(
            room?.basePrice ??
            room?.channelManagerRate ??
            room?.todayPrice ??
            room?.price ??
            0
          );


        /* ===================================================
           STAY DATES
        =================================================== */

        const stayDates =
          getStayDates(
            searchDetails?.checkIn,
            searchDetails?.checkOut
          );


        /* ===================================================
           PUBLIC NIGHTLY RATES
        =================================================== */

        const publicNightlyRates =
          Array.isArray(
            roomRateData?.rates
          )
            ? roomRateData.rates
            : [];


        /* ===================================================
           RATE MAP
        =================================================== */

        const rateMap =
          publicNightlyRates.reduce(
            (
              map,
              item
            ) => {

              map[item.date] =
                Number(
                  item.rate || 0
                );

              return map;

            },
            {}
          );


        /* ===================================================
           COMPLETE NIGHTLY RATES
           
           IMPORTANT:
           We only fallback when the public endpoint
           does not return a particular date.
        =================================================== */

        const completeNightlyRates =
          stayDates.length > 0

            ? stayDates.map(
                (date) => {

                  const hasPublicRate =
                    Object.prototype.hasOwnProperty.call(
                      rateMap,
                      date
                    );


                  return {

                    date,

                    rate:
                      hasPublicRate

                        ? Number(
                            rateMap[
                              date
                            ]
                          )

                        : fallbackRate,

                    source:
                      hasPublicRate

                        ? "PUBLIC_RATE"

                        : "BASE_PRICE",

                  };

                }
              )

            : [];


        /* ===================================================
           CHECK-IN RATE
        =================================================== */

        const checkInRate =
          completeNightlyRates.find(
            (item) =>
              item.date ===
              selectedRateDate
          );


        const finalRate =
          Number(
            checkInRate?.rate ??
            fallbackRate
          );


        /* ===================================================
           ROOM TOTAL
        =================================================== */

        const roomTotal =
          completeNightlyRates.length > 0

            ? completeNightlyRates.reduce(
                (
                  total,
                  night
                ) =>
                  total +
                  Number(
                    night.rate || 0
                  ),
                0
              )

            : finalRate;


        /* ===================================================
           DEBUG
        =================================================== */

        console.log(
          "💰 ROOM PUBLIC RATES:",
          {
            room:
              room?.name,

            roomTypeId,

            checkIn:
              searchDetails?.checkIn,

            checkOut:
              searchDetails?.checkOut,

            selectedRateDate,

            publicNightlyRates,

            completeNightlyRates,

            finalRate,

            roomTotal,

          }
        );


        /* ===================================================
           RETURN ROOM
        =================================================== */

        return {

          ...room,

          price:
            finalRate,

          todayPrice:
            finalRate,

          channelManagerRate:
            finalRate,

          rateDate:
            selectedRateDate,

          nightlyRates:
            completeNightlyRates,

          roomTotal,

        };

      }
    );


  /* =========================================================
     RETURN
  ========================================================= */

  return {

    rooms:
      roomsWithRates,

    roomsWithChannelManagerRates:
      roomsWithRates,

    ratesLoading,

    ratesByRoom,

  };

};


export default useBookingRates;